import React, { useState } from 'react';
import { Text, StyleSheet, View, TouchableOpacity, ScrollView } from 'react-native';
import EmptyCredentialIcon from '../../../components/icons/EmptyCredentialIcon';
import GenericButton from '../../../components/molecules/buttons/GenericButton';
import DeleteConfirmModal from '../../../modal/DeleteConfirmModal';
import Layout from '../../../components/layout/Layout';
import { useIssuerService } from '../../../services';
import { useTenantContext, useToastContext } from '../../../contexts';
import GenericBack from '../../../components/molecules/buttons/GenericBack';
import { IInvitation } from '../../../types/invitation';
import SendUserInviteModal from '../../../modal/SendUserInviteModal';
import ToastProvider from '../../../contexts/ToastContext';
import Icon from '@mdi/react';
import { mdiEye, mdiEyeOff } from '@mdi/js';
import { buildUrl } from '../../../utils/Utils';

interface IInvitationUserDetailsProps {
  user: IInvitation;
  setVisible: (value: boolean) => void;
}

/**
 * InvitationUserDetails component
 *
 * @param {*} { user, setVisible }
 * @return {*}
 */
const InvitationUserDetails: React.FC<IInvitationUserDetailsProps> = ({ user, setVisible }) => {
  const issuerService = useIssuerService();
  const toast = useToastContext();

  const { refresh } = useTenantContext();

  const inviteLink = buildUrl(window.location.href + 'user/invite/' + user?.reservation_id);

  const [resendInvitationModalVisible, setResendInvitationModalVisible] = useState(false);
  const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isInviteLinkVisible, setIsInviteLinkVisible] = useState(false);

  const openResendInvitationModal = () => {
    setResendInvitationModalVisible(true);
  };

  const closeResendInvitationModal = () => {
    setResendInvitationModalVisible(false);
  };

  const openDeleteUserModal = () => {
    setDeleteUserModalVisible(true);
  };

  const closeDeleteUserModal = () => {
    setDeleteUserModalVisible(false);
  };

  const deleteUser = async () => {
    try {
      // Delete user without wallet
      await issuerService.deleteUserReservation(user.reservation_id);
      toast.showToast('User deleted successfully', 3000);
      refresh();
      setVisible(false);
    } catch (error) {
      console.error(error);
      toast.showToast('Failed to delete user', 3000, false);
    }
  };

  return (
    <ToastProvider>
      <Layout title="Issuer Application">
        <DeleteConfirmModal
          isVisible={deleteUserModalVisible}
          onClose={closeDeleteUserModal}
          onConfirm={deleteUser}
        />
        <SendUserInviteModal
          isVisible={resendInvitationModalVisible}
          onClose={closeResendInvitationModal}
          invitation={user}
        />
        <ScrollView style={styles.container}>
          <GenericBack onPress={() => setVisible(false)} buttonStyle={styles.backButton} />

          {/* User Information Card */}
          <View style={styles.userCard}>
            <View style={styles.userHeader}>
              <View style={styles.avatarContainer}>
                <Text style={styles.avatarText}>
                  {user?.tenant_name?.charAt(0).toUpperCase() || 'U'}
                </Text>
              </View>
              <View style={styles.userInfo}>
                <Text style={styles.nameHeader}>{user?.tenant_name}</Text>
                <Text style={styles.emailText}>{user?.email}</Text>
                <Text style={styles.statusText}>Invitation Pending</Text>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <GenericButton
                title={'Resend Invitation'}
                onPress={openResendInvitationModal}
                buttonStyle={styles.resendButton}
                textStyle={styles.buttonText}
                isPrimary={false}
              />
              <GenericButton
                title={'Delete Invitation'}
                onPress={openDeleteUserModal}
                buttonStyle={styles.deleteButton}
                textStyle={styles.buttonText}
                isPrimary={false}
              />
            </View>
          </View>

          {/* Invitation Details Card */}
          <View style={styles.detailsCard}>
            <Text style={styles.sectionTitle}>Invitation Details</Text>

            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Invitation ID:</Text>
              <Text style={styles.detailValue}>{user?.reservation_id}</Text>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Password:</Text>
              <View style={styles.passwordContainer}>
                <Text style={styles.passwordText}>
                  {isPasswordVisible ? user?.reservation_pwd : '*****************'}
                </Text>
                <TouchableOpacity
                  onPress={() => setIsPasswordVisible(!isPasswordVisible)}
                  style={styles.eyeButton}>
                  <Icon path={isPasswordVisible ? mdiEyeOff : mdiEye} size={0.8} color="#6c757d" />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Invitation Link:</Text>
              <View style={styles.passwordContainer}>
                <Text style={[styles.passwordText, styles.linkText]} numberOfLines={1}>
                  {isInviteLinkVisible ? inviteLink : '*****************'}
                </Text>
                <TouchableOpacity
                  onPress={() => setIsInviteLinkVisible(!isInviteLinkVisible)}
                  style={styles.eyeButton}>
                  <Icon
                    path={isInviteLinkVisible ? mdiEyeOff : mdiEye}
                    size={0.8}
                    color="#6c757d"
                  />
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* Status Card */}
          <View style={styles.statusCard}>
            <View style={styles.emptyStateContainer}>
              <EmptyCredentialIcon />
              <Text style={styles.emptyTitle}>No Wallet Connected</Text>
              <Text style={styles.emptySubText}>
                {user?.tenant_name} has not accepted their invitation yet.
              </Text>
              <Text style={styles.emptySubText}>Send a reminder to help them get started.</Text>
              <GenericButton
                title={'Resend Invitation'}
                onPress={openResendInvitationModal}
                buttonStyle={styles.resendButton}
                textStyle={styles.buttonText}
                isPrimary={true}
              />
            </View>
          </View>
        </ScrollView>
      </Layout>
    </ToastProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '60%'
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 16,
    marginBottom: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    alignSelf: 'flex-start'
  },
  userCard: {
    backgroundColor: '#f8f9fa',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderLeftWidth: 4,
    borderLeftColor: '#ffc107'
  },
  userHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20
  },
  avatarContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#ffc107',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16
  },
  avatarText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: '600'
  },
  userInfo: {
    flex: 1
  },
  nameHeader: {
    fontSize: 24,
    fontWeight: '700',
    color: '#212529',
    marginBottom: 4
  },
  emailText: {
    fontSize: 16,
    fontWeight: '400',
    color: '#6c757d',
    marginBottom: 4
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffc107'
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12
  },
  resendButton: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffc107'
  },
  deleteButton: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#dc3545'
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff'
  },
  detailsCard: {
    backgroundColor: '#f8f9fa',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#495057',
    marginBottom: 16
  },
  detailRow: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef'
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6c757d',
    marginBottom: 4
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '400',
    color: '#212529'
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  passwordText: {
    fontSize: 14,
    fontWeight: '400',
    color: '#212529',
    fontFamily: 'monospace',
    flex: 1
  },
  linkText: {
    fontSize: 12
  },
  eyeButton: {
    padding: 4,
    marginLeft: 8
  },
  statusCard: {
    backgroundColor: '#f8f9fa',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  emptyStateContainer: {
    alignItems: 'center',
    paddingVertical: 20,
    gap: 16
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#495057',
    textAlign: 'center'
  },
  emptySubText: {
    fontSize: 16,
    fontWeight: '400',
    color: '#6c757d',
    textAlign: 'center',
    lineHeight: 22
  }
});

export default InvitationUserDetails;
